import React from 'react';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import eventRefs from '../../assets/eventRef';
import { isNeutralConnectionEvent } from '../../utils/connectionEventHistory';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  TIMEFRAME_PRESETS,
  DEFAULT_TIMEFRAME_KEY,
  getDefaultTimeRange,
  createRangeFromDuration,
  formatTimeRangeLabel,
  findTimeframePreset,
} from '../../utils/timeRange';

const EVENT_REF_MAP = new Map((eventRefs || []).map((ref) => [String(ref.code), ref]));
const EVENT_GROUP_ORDER = [
  'Network & Ping',
  'Inlet Events',
  'Outlet Events',
  'Bank Events',
  'Environmental Events',
  'Sensor & IO Events',
  'Scheduled Events',
  'ATS Events',
  'Other Events',
];

function categorizeEventGroup(ref) {
  const name = (ref?.name || '').toLowerCase();
  if (name.includes('autoping') || name.includes('network') || name.includes('ping')) {
    return 'Network & Ping';
  }
  if (name.startsWith('inlet')) return 'Inlet Events';
  if (name.startsWith('outlet')) return 'Outlet Events';
  if (name.startsWith('bank')) return 'Bank Events';
  if (name.includes('temperature') || name.includes('humidity')) return 'Environmental Events';
  if (
    name.includes('sensor') ||
    name.includes('digital') ||
    name.includes('switch') ||
    name.includes('hub')
  ) {
    return 'Sensor & IO Events';
  }
  if (name.includes('schedule')) return 'Scheduled Events';
  if (name.includes('ats')) return 'ATS Events';
  return 'Other Events';
}

const EVENT_LOG_DEFAULT_TIMEFRAME = '30d';

const buildInitialTimeRangeState = () => {
  const range = getDefaultTimeRange(EVENT_LOG_DEFAULT_TIMEFRAME);
  return {
    timeRangeKey: EVENT_LOG_DEFAULT_TIMEFRAME,
    appliedRange: range,
    customRange: [new Date(range.since), new Date(range.before)],
    customError: '',
  };
};

export default class LoggingEventLogs extends React.Component {
  state = {
    page: 1,
    limit: 20,
    query: '',
    searchText: '',
    eventTypes: [],
    alertsOnly: false,
    eventId: '',
    ...buildInitialTimeRangeState(),
    deviceLogs: [],
    connectionMonitorStatus: null,
    connectionMonitorStatusError: false,
    deviceMeta: {},
    deviceLoading: false,
    exportingCsv: false,
    dark: typeof document !== 'undefined' && document.body.classList.contains('dark-mode'),
  };

  componentDidMount() {
    this.mounted = true;
    const params = new URLSearchParams(this.props.location?.search || '');
    const eventId = params.get('eventId') || '';
    if (eventId) {
      this.setState({ eventId }, () => this.fetchPage(1));
    } else {
      this.fetchPage(1);
    }
    if (typeof document !== 'undefined') {
      this.darkObserver = new MutationObserver(() => {
        const isDark = document.body.classList.contains('dark-mode');
        this.setState((prev) => (prev.dark === isDark ? null : { dark: isDark }));
      });
      this.darkObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    this.darkObserver && this.darkObserver.disconnect();
  }

  getTimeRangeParams() {
    const { appliedRange } = this.state;
    const params = {};
    if (appliedRange && Number.isFinite(appliedRange.since)) {
      params.since = Math.floor(appliedRange.since);
    }
    if (appliedRange && Number.isFinite(appliedRange.before)) {
      params.before = Math.floor(appliedRange.before);
    }
    return params;
  }

  handleTimePresetChange = (event) => {
    const nextKey = event?.target?.value || EVENT_LOG_DEFAULT_TIMEFRAME;
    const currentKey = this.state.timeRangeKey;
    if (nextKey === currentKey) return;
    if (nextKey === 'custom') {
      this.setState({ timeRangeKey: nextKey, customError: '' });
      return;
    }
    const preset = findTimeframePreset(nextKey);
    const duration = preset?.durationMs || null;
    const range = duration ? createRangeFromDuration(duration) : this.state.appliedRange;
    this.setState(
      {
        timeRangeKey: nextKey,
        appliedRange: range,
        customRange: [new Date(range.since), new Date(range.before)],
        customError: '',
      },
      () => this.fetchPage(1)
    );
  };

  handleCustomRangeChange = (dates) => {
    const [start, end] = dates || [];
    this.setState({ customRange: [start, end], customError: '' });
  };

  handleApplyCustomRange = () => {
    const [start, end] = this.state.customRange || [];
    if (!start || !end) {
      this.setState({ customError: 'Select a start and end time.' });
      return;
    }
    if (start.getTime() >= end.getTime()) {
      this.setState({ customError: 'Start time must be before end time.' });
      return;
    }
    const range = { since: start.getTime(), before: end.getTime() };
    this.setState(
      {
        appliedRange: range,
        customRange: [start, end],
        customError: '',
        timeRangeKey: 'custom',
      },
      () => this.fetchPage(1)
    );
  };

  handleResetCustomRange = () => {
    const { appliedRange } = this.state;
    const start = appliedRange?.since ? new Date(appliedRange.since) : new Date();
    const end = appliedRange?.before ? new Date(appliedRange.before) : new Date();
    this.setState({ customRange: [start, end], customError: '' });
  };

  buildQueryParams = ({ page, limit, includePagination = true } = {}) => {
    const {
      query,
      eventTypes,
      alertsOnly,
      eventId,
      searchText,
    } = this.state;
    const base = {
      search: query || '',
      eventType: (eventTypes || []).join(','),
      triggered: alertsOnly ? 'true' : '',
      eventId: eventId || '',
      ...this.getTimeRangeParams(),
      ...(this.props.queryParams || {}),
    };

    if (includePagination) {
      base.page = page != null ? page : this.state.page;
      base.limit = limit != null ? limit : this.state.limit;
    }

    return base;
  };

  handleExportCsv = async () => {
    if (this.state.exportingCsv) {
      return;
    }
    this.setState({ exportingCsv: true });
    try {
      const params = this.buildQueryParams({ includePagination: false });
      const res = await axios.get('/v2/eventLogs/export', {
        params,
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = url;
      link.download = `event-logs-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export event logs CSV', error);
    } finally {
      this.setState({ exportingCsv: false });
    }
  };

  handleEventTypeSelect = (event) => {
    const value = event.target.value;
    if (!value) return;
    if (this.state.eventTypes.includes(value)) {
      event.target.value = '';
      return;
    }
    this.handleFilterChange({ eventTypes: [...this.state.eventTypes, value] });
    event.target.value = '';
  };

  handleRemoveEventType = (code) => {
    this.handleFilterChange({
      eventTypes: this.state.eventTypes.filter((c) => c !== code),
    });
  };

  getEventTypeGroups() {
    const groups = new Map();
    (eventRefs || []).forEach((ref) => {
      if (!ref || typeof ref.code === 'undefined') return;
      const groupLabel = categorizeEventGroup(ref);
      if (!groups.has(groupLabel)) groups.set(groupLabel, []);
      groups.get(groupLabel).push({
        value: String(ref.code),
        label: ref.name || String(ref.code),
      });
    });
    const ordered = EVENT_GROUP_ORDER.filter((label) => groups.has(label));
    const leftovers = Array.from(groups.keys())
      .filter((label) => !EVENT_GROUP_ORDER.includes(label))
      .sort();
    return [...ordered, ...leftovers].map((label) => ({
      label,
      options: (groups.get(label) || []).sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }

  fetchPage = (page) => {
    const { fetchResourcePaginate, device } = this.props;
    const params = this.buildQueryParams({ page });
    this.setState({ page, connectionMonitorStatus: null, connectionMonitorStatusError: false });
    const statusRequest = (this.statusRequest || 0) + 1;
    this.statusRequest = statusRequest;
    axios.get('/v1/connection-history', {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
    }).then((res) => {
      if (this.mounted && this.statusRequest === statusRequest) {
        this.setState({ connectionMonitorStatus: res.data.status, connectionMonitorStatusError: false });
      }
    }).catch(() => {
      if (this.mounted && this.statusRequest === statusRequest) {
        this.setState({ connectionMonitorStatus: null, connectionMonitorStatusError: true });
      }
    });
    if (device) {
      this.setState({ deviceLoading: true });
      axios
        .get(`/v2/eventLogs`, {
          params: {
            ...params,
            enclosure_serial_number: device.enclosureSerialNumber,
          },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        })
        .then((res) => {
          this.setState({
            deviceLogs: res.data.data,
            deviceMeta: res.data.meta,
            deviceLoading: false,
          });
        })
        .catch((err) => {
          console.error('Error fetching device event logs', err);
          this.setState({ deviceLoading: false });
        });
    } else if (fetchResourcePaginate) {
      // If your API doesn't support these filters yet, it will ignore them.
      fetchResourcePaginate('eventLogs', undefined, {
        ...params,
      });
    }
  };

  handleFilterChange = (next) => {
    this.setState(next, () => this.fetchPage(1));
  };

  applySearch = () => {
    this.handleFilterChange({ query: this.state.searchText, eventId: '' });
  };

  clearAllFilters = () => {
    this.handleFilterChange({
      query: '',
      searchText: '',
      eventTypes: [],
      alertsOnly: false,
      eventId: '',
    });
  };

  renderFilterChips(logs) {
    const { query, alertsOnly, eventId } = this.state;
    const eventName = logs.find(log => log.eventId == eventId)?.name || '';
    const chips = [];

    if (eventId) {
      chips.push(
        <div
          key="event"
          className="button secondary"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            // background: '#eee',
            padding: '10px 20px',
            borderRadius: 4,
          }}
        >
          <span>
            Event {eventName || eventId}{query ? ` @ ${query}` : ''}
          </span>
          <button
            type="button"
            onClick={() =>
              this.handleFilterChange({ query: '', searchText: '', eventId: '' })
            }
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            <i className="material-icons" style={{ fontSize: 14 }}>close</i>
          </button>
        </div>
      );
    } 

    return chips;
  }

  renderRelativeTime(ts) {
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    return formatDistanceToNow(d, { addSuffix: true });
  }

  renderTimestamp(ts) {
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    return format(d, 'PP p');
  }

  statusDot(connected) {
    return (
      <i
        className="material-icons"
        title={connected ? 'Online' : 'Offline'}
        style={{
          fontSize: 12,
          verticalAlign: 'middle',
          margin: "0px",
          width: "18px",
          // marginRight: 6,
          color: connected ? 'forestgreen' : '#9e9e9e',
        }}
      >
        fiber_manual_record
      </i>
    );
  }

  deviceCell(log) {
    const connected = log.connectionState === 'Connected';
    const label = log.deviceName || log.enclosureSerialNumber;
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          {this.statusDot(connected)}
          <Link
            to={`/devices/connected/${log.enclosureSerialNumber}/details`}
            style={{ textDecoration: 'underline' }}
            title="Open device"
          >
            {label}
          </Link>
        </div>
        <div style={{ fontSize: '80%', color: '#666', display: 'flex', gap: 8, marginTop: 2 }}>
          <span>SN: {log.enclosureSerialNumber}</span>
          {/* <button
            type="button"
            aria-label="Copy serial"
            title="Copy serial"
            onClick={() => navigator.clipboard?.writeText(String(log.enclosureSerialNumber))}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <i className="material-icons" style={{ fontSize: 14, color: '#666' }}>
              content_copy
            </i>
          </button> */}
        </div>
      </div>
    );
  }

  renderStatus(log) {
    if (isNeutralConnectionEvent(log)) return <span>Info</span>;
    const isActive = !!log.triggered;
    return (
      <span style={{ color: isActive ? 'maroon' : 'forestgreen' }}>
        {isActive ? 'Active' : 'Cleared'}
      </span>
    );
  }

  renderType(log) {
    const ref = eventRefs.find((e) => e.code == log.eventType);
    const name = ref ? ref.name : log.eventType;
    return name || '';
  }

  renderDetails(log) {
    if (Number(log.eventType) === 1048267 && isNeutralConnectionEvent(log)) {
      return 'Device connected to CMP. This historical entry does not confirm a preceding sustained outage.';
    }
    const ref = eventRefs.find((e) => e.code == log.eventType);
    let params = [];
    try {
      params = Array.isArray(log.params)
        ? log.params
        : log.params
        ? JSON.parse(log.params)
        : [];
    } catch (e) {
      params = [];
    }
    if (ref) {
      const tpl = log.triggered ? ref.triggeredDescription : ref.notTriggeredDescription;
      if (tpl) {

        return tpl.replace(/{{param(\d+)}}/g, (_, i) => {
          const idx = parseInt(i, 10) - 1;
          let val = params[idx] !== undefined ? String(params[idx]) : '';
          if (log.eventType == 12 && idx === 2) {
            if (val === '1') return 'primary ethernet';
            if (val === '2') return 'USB secondary ethernet';
            if (val === '' ) return "primary ethernet"
          }
          return val;        
        });
      }
    }
    return params.length ? JSON.stringify(params) : '';
  }

  parseActionNames(actions) {
    // Accepts:
    // - array of strings: ["rebootOutlet", "email"]
    // - array of objects: [{name:"rebootOutlet",...}]
    // - object or JSON string of either form
    let a = actions;
    if (typeof a === 'string') {
      try {
        a = JSON.parse(a);
      } catch {
        a = [];
      }
    }
    if (!a) return [];
    if (Array.isArray(a)) {
      return a
        .map((v) => (typeof v === 'string' ? v : v && typeof v.name === 'string' ? v.name : null))
        .filter(Boolean);
    }
    if (typeof a === 'object') {
      // if object like {actions:[...]} or keyed map
      if (Array.isArray(a.actions)) {
        return this.parseActionNames(a.actions);
      }
      // if map of {rebootOutlet: {...}, email: {...}}
      return Object.keys(a);
    }
    return [];
  }

  getEventRef(log) {
    if (!log) return null;
    return EVENT_REF_MAP.get(String(log.eventType)) || null;
  }

  stripTrailingVerbs(text) {
    if (!text) return '';
    return text.replace(/\b(Triggered|Cleared|Activated|Surpassed|Resolved|Updated)\b$/i, '').trim();
  }

  getOperationBaseName(log) {
    const ref = this.getEventRef(log);
    const base = ref?.operationName || log.name || ref?.name || '';
    return this.stripTrailingVerbs(base);
  }

  getOperationVerbs(ref, baseName) {
    const normalized = (baseName || '').toLowerCase();
    const shouldSurpass = /\b(threshold|timeout|limit|max|min|trip|over|under)\b/.test(normalized);
    const triggeredFallback = shouldSurpass ? 'Surpassed' : 'Activated';
    return {
      triggered: (ref?.operationVerbs?.triggered || triggeredFallback).trim(),
      cleared: (ref?.operationVerbs?.cleared || 'Cleared').trim(),
    };
  }

  getOperationPhrase(log, baseName) {
    if (isNeutralConnectionEvent(log)) return 'Device connected';
    const ref = this.getEventRef(log);
    const verbs = this.getOperationVerbs(ref, baseName);
    const word = log.triggered ? verbs.triggered : verbs.cleared;
    const safeBase = baseName || log.name || ref?.name || 'Event';
    return `${safeBase} ${word}`.trim();
  }

  renderDescriptionContent(log, details) {
    const hasDetails = !!details;
    const eventLabel = log?.name || '';
    const ref = this.getEventRef(log);
    const allowLink = !!eventLabel && ref && !ref.disableEventLink;
    return (
      <div
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        title={
          hasDetails
            ? `${details}${eventLabel ? ` (${eventLabel})` : ''}`
            : eventLabel
        }
      >
        {hasDetails ? details : '—'}
        {eventLabel && (
          <span style={{ color: '#6b7280' }}>
            {' '}
            (
            {allowLink ? (
              <Link
                to={`/devices/connected/${log.enclosureSerialNumber}/alerts/${log.eventId}`}
                style={{ textDecoration: 'underline' }}
              >
                {eventLabel}
              </Link>
            ) : (
              <span>{eventLabel}</span>
            )}
            )
          </span>
        )}
      </div>
    );
  }

  actionsCell(log) {
    const names = this.parseActionNames(log.actions);
    if (!names.length) return <span style={{ color: '#999' }}>—</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {names.map((n) => (
          <span
            key={n}
            // className="spreadsheet-badge"
            title={n}
            // style={{ backgroundColor: '#eef2ff', color: '#1f2a6b' }}
          >
            {n}
          </span>
        ))}
      </div>
    );
  }

  render() {
    let logs, loading, meta;
    if (this.props.device) {
      logs = this.state.deviceLogs;
      loading = this.state.deviceLoading;
      meta = this.state.deviceMeta;
    } else {
      const {
        eventLogsPages = [],
        eventLogsMeta = {},
        eventLogsPageLoading = {},
      } = this.props.resources;

      const currentPage = eventLogsPages.find((p) => p.page === this.state.page) || {};
      logs = currentPage.eventLogs || [];
      loading = !!eventLogsPageLoading[this.state.page];
      meta = eventLogsMeta;
    }

    const currentPage = this.state.page || 1;
    const pageLimit = this.state.limit || logs.length || 1;
    const totalCount = typeof meta?.totalCount === "number" ? meta.totalCount : undefined;
    let summaryText = null;
    if (typeof totalCount === "number" && !(loading && logs.length === 0)) {
      if (totalCount === 0) {
        summaryText = "Showing 0 of 0 event logs";
      } else if (logs.length === 0) {
        summaryText = `Showing 0 of ${totalCount} event logs`;
      } else {
        const showingStart = (currentPage - 1) * pageLimit + 1;
        const showingEnd = Math.min(showingStart + logs.length - 1, totalCount);
        summaryText = `Showing ${showingStart}-${showingEnd} of ${totalCount} event logs`;
      }
    }

    const eventTypeOptions = this.getEventTypeGroups();
    const {
      query,
      eventTypes,
      alertsOnly,
      timeRangeKey,
      customRange,
      customError,
      appliedRange,
    } = this.state;
    const rangeLabel = formatTimeRangeLabel(appliedRange?.since, appliedRange?.before);
    const disableCustomApply = !(customRange[0] && customRange[1]);
    const outerClass = this.props.embedded ? "" : "grid-container";
    const innerClass = this.props.embedded ? "" : "grid-x grid-padding-x grid-padding-y align-left";

    return (
      <div className={outerClass}>
        <div className={innerClass}>
          <div className="cell">
            <h4 style={{ paddingLeft: '15px', marginBottom: "25px" }}>Event Logs</h4>

            {/* Filters */}
            <div
              style={{
                padding: '0 15px 12px 15px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div className="grid-x">
                <div className="cell large-6 medium-6">
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      placeholder="Search device name, serial, or event…"
                      value={this.state.searchText}
                      onChange={(e) => this.setState({ searchText: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && this.applySearch()}
                      style={{ flex: 1 }}
                    />
                    {/* <button type="button" className="button" onClick={this.applySearch}>
                      Search
                    </button> */}
                    <span
                      className="search-icon-button"
                      style={{ margin: "0 0 1rem" }}
                      onClick={() => {
                        this.applySearch();
                      }}
                    >
                      <i className="material-icons">search</i>
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 280 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: '85%', color: '#4b5563' }}>
                    <span style={{ fontWeight: 600, marginBottom: 4 }}>Filter by event type</span>
                    <select
                      defaultValue=""
                      onChange={this.handleEventTypeSelect}
                      style={{ minWidth: 260 }}
                    >
                      <option value="" disabled>
                        Select event type…
                      </option>
                      {eventTypeOptions.map(({ label, options }) => (
                        <optgroup key={label} label={label}>
                          {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {eventTypes.length === 0 && (
                      <span style={{ fontSize: '80%', color: '#9ca3af' }}>No event types selected</span>
                    )}
                    {eventTypes.map((code) => {
                      const label =
                        EVENT_REF_MAP.get(String(code))?.name || `Event ${code}`;
                      return (
                        <span
                          key={code}
                          className="button secondary"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '6px 12px',
                            borderRadius: 999,
                            fontSize: '80%',
                          }}
                        >
                          <span>{label}</span>
                          <button
                            type="button"
                            onClick={() => this.handleRemoveEventType(code)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              cursor: 'pointer',
                              lineHeight: 1,
                            }}
                          >
                            <i className="material-icons" style={{ fontSize: 14 }}>close</i>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center',  }}>
                  <input
                    type="checkbox"
                    checked={this.state.alertsOnly}
                    style={{
                      
                    }}
                    onChange={(e) =>
                      this.handleFilterChange({ alertsOnly: e.target.checked })
                    }
                  />
                  <span
                    style={{
                      // dont allow text break
                      marginBottom: "1rem"
                    }}
                  >
                    Alerts only
                  </span>
                </label>
                {this.renderFilterChips(logs)}
                {(this.state.query ||
                  (this.state.eventTypes && this.state.eventTypes.length > 0) ||
                  this.state.alertsOnly ||
                  this.state.eventId) && (
                  <button
                    type="button"

                    onClick={this.clearAllFilters}
                    style={{ marginLeft: 'auto', cursor: 'pointer' }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '85%', color: '#4b5563' }}>
                  <span style={{ fontWeight: 600, marginBottom: 4 }}>Timeframe</span>
                  <select
                    value={timeRangeKey}
                    onChange={this.handleTimePresetChange}
                    style={{ minWidth: 160 }}
                  >
                    {TIMEFRAME_PRESETS.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                {rangeLabel && (
                  <span style={{ fontSize: '85%', color: '#6b7280' }}>
                    {rangeLabel}
                  </span>
                )}
              </div>
            </div>
            {timeRangeKey === 'custom' && (
              <div
                style={{
                  padding: '0 15px 12px 15px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <DatePicker
                  selected={customRange[0]}
                  onChange={this.handleCustomRangeChange}
                  startDate={customRange[0]}
                  endDate={customRange[1]}
                  selectsRange
                  showTimeSelect
                  dateFormat="Pp"
                  maxDate={new Date()}
                  className="event-logs__datepicker"
                />
                {customError && (
                  <div style={{ color: '#b91c1c', fontSize: '80%' }}>{customError}</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={this.handleResetCustomRange}>
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={this.handleApplyCustomRange}
                    disabled={disableCustomApply}
                    style={{
                      opacity: disableCustomApply ? 0.6 : 1,
                      cursor: disableCustomApply ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: '#6b7280',
                fontSize: '14px',
                margin: '0 15px 8px',
                gap: 12,
              }}
            >
              <span>{summaryText || ''}</span>
              <button
                type="button"
                onClick={this.handleExportCsv}
                disabled={this.state.exportingCsv || loading}
                style={{
                  cursor: this.state.exportingCsv || loading ? 'not-allowed' : 'pointer',
                }}
              >
                {this.state.exportingCsv ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>

            {this.state.connectionMonitorStatusError && (
              <p role="status" style={{ margin: '0 15px 12px' }}>Connection monitoring status is unavailable.</p>
            )}
            {this.state.connectionMonitorStatus && this.state.connectionMonitorStatus.state !== 'enabled' && (
              <p role="status" style={{ margin: '0 15px 12px' }}>
                Connection outage tracking is {this.state.connectionMonitorStatus.state === 'starting' ? 'starting' : 'degraded'}. Device controls remain available.
              </p>
            )}
            {loading ? (
              <EventLogsSkeleton device={this.props.device} />
            ) : logs.length > 0 ? (
              <table
                className="unstriped"
                style={{ borderSpacing: '0px 3px', borderCollapse: 'separate' }}
              >
                <thead>
                  <tr>
                    {!this.props.device && <th>Device</th>}
                    <th>Operation</th>
                    <th>Status</th>
                    <th>Time</th>
                    <th>Timestamp</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const details = this.renderDetails(log);
                    const typeLabel = this.renderType(log);
                    const baseName = typeLabel || this.getOperationBaseName(log);
                    const operationPhrase = this.getOperationPhrase(log, baseName);
                    return (
                      <tr key={log.logId}>
                        {!this.props.device && <td>{this.deviceCell(log)}</td>}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{operationPhrase}</span>
                            <button
                              type="button"
                              title="Filter for this event"
                              onClick={() =>
                                this.handleFilterChange(
                                  this.props.device
                                    ? { eventId: String(log.eventId) }
                                    : {
                                        query: String(log.enclosureSerialNumber),
                                        searchText: String(log.enclosureSerialNumber),
                                        eventId: String(log.eventId),
                                      }
                                )
                              }
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 20,
                                width: 20,
                              }}
                            >
                              <i className="material-icons" style={{ fontSize: 16, color: '#666' }}>
                                filter_list
                              </i>
                            </button>
                          </div>
                        </td>
                        <td>{this.renderStatus(log)}</td>
                        <td>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {this.renderRelativeTime(log.deviceTs)}
                          </span>
                        </td>
                        <td>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {this.renderTimestamp(log.deviceTs)}
                          </span>
                        </td>
                        <td style={{ maxWidth: 420 }}>
                          {this.renderDescriptionContent(log, details)}
                        </td>
                        <td>{this.actionsCell(log)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '15px' }}>No event logs found</div>
            )}

            <ReactPaginate
              pageCount={meta.totalPages || 1}
              forcePage={(this.state.page || 1) - 1}
              onPageChange={({ selected }) => this.fetchPage(selected + 1)}
              containerClassName="react-paginate"
              activeClassName="selected"
              previousLabel="Previous"
              nextLabel="Next"
            />
          </div>
        </div>
      </div>
    );
  }
}

const EventLogsSkeleton = ({ device }) => (
  <table className="unstriped" style={{ borderSpacing: '0px 5px' }}>
    <thead>
      <tr>
        {!device && <th>Device</th>}
        <th>Operation</th>
        <th>Status</th>
        <th>Time</th>
        <th>Timestamp</th>
        <th>Description</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {[...Array(5)].map((_, i) => (
        <tr key={i}>
          {!device && (
            <td>
              <div className="skeleton-blank" />
            </td>
          )}
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);
